import { readProofField, readRecord } from "./source-to-application-quality";

export interface HeadlessAuthorityProofRef {
  status?: string;
  evidenceRef?: string;
}

export interface HeadlessSourceDocumentProfile extends HeadlessAuthorityProofRef {
  profileKind?: string;
  sourceComplianceMode?: string;
  storyAnchorCount?: number;
  rootName?: string;
}

export interface HeadlessSpecFlowOntology extends HeadlessAuthorityProofRef {
  ontologyVersion?: string;
  sourceComplianceMode?: string;
  sourceScope?: string;
  absentUiPolicy?: string;
}

export interface HeadlessApplicationProof extends HeadlessAuthorityProofRef {
  cogoJsonRef?: string;
  readiness?: string;
  surfaceCount?: number;
  rawFigmaExtractionGapCount?: number;
  sourceCoverageUnassembledFactCount?: number;
  sourceAuthorityViolationCount?: number;
}

export interface HeadlessRuntimeProof extends HeadlessAuthorityProofRef {
  finalSurfaceRef?: string;
  finalStoryRef?: string;
  finalText?: string;
}

export interface HeadlessVideoProof extends HeadlessRuntimeProof {
  videoRef?: string;
  posterRef?: string;
  accountUxNotMounted?: boolean | string;
}

export interface HeadlessAuthorityPolicy {
  sourceComplianceMode?: string;
  excludedSourceScopes?: string[];
  modelMayPatchCogoJsonDirectly?: boolean;
  studioMaySilentlyMutateCogoJson?: boolean;
  changeRoute?: string;
}

export interface StudioHeadlessAuthorityBundle {
  schemaVersion: "studio-headless-authority-bundle.v1";
  status: string;
  artifactId?: string;
  title?: string;
  generatedAt?: string;
  sourceDocumentProfile?: HeadlessSourceDocumentProfile;
  specFlowOntology?: HeadlessSpecFlowOntology;
  application?: HeadlessApplicationProof;
  runtimeE2E?: HeadlessRuntimeProof;
  storyCoverage?: HeadlessAuthorityProofRef;
  overlapAudit?: HeadlessAuthorityProofRef;
  videoProof?: HeadlessVideoProof;
  policy?: HeadlessAuthorityPolicy;
  evidenceRefs?: Record<string, string>;
}

const CANDIDATE_KEYS = [
  "headlessAuthorityBundle",
  "wonBankingHeadlessAuthorityBundle",
  "studioHeadlessAuthorityBundle",
  "sourceAuthorityBundle",
];

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function readStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const strings = value.filter(
    (item): item is string => typeof item === "string" && item.trim() !== "",
  );
  return strings.length > 0 ? strings : undefined;
}

function readBooleanOrString(value: unknown): boolean | string | undefined {
  if (typeof value === "boolean") return value;
  return readString(value);
}

function readProofRef(value: unknown): HeadlessAuthorityProofRef | undefined {
  const record = readRecord(value);
  if (!record) {
    const evidenceRef = readString(value);
    return evidenceRef ? { evidenceRef } : undefined;
  }
  return {
    status: readString(record.status),
    evidenceRef: readString(record.evidenceRef) ?? readString(record.ref),
  };
}

function readSourceDocumentProfile(
  value: unknown,
): HeadlessSourceDocumentProfile | undefined {
  const record = readRecord(value);
  if (!record) {
    const evidenceRef = readString(value);
    return evidenceRef ? { evidenceRef } : undefined;
  }
  return {
    ...readProofRef(record),
    profileKind: readString(record.profileKind),
    sourceComplianceMode: readString(record.sourceComplianceMode),
    storyAnchorCount: readNumber(record.storyAnchorCount),
    rootName: readString(record.rootName),
  };
}

function readSpecFlowOntology(
  value: unknown,
): HeadlessSpecFlowOntology | undefined {
  const record = readRecord(value);
  if (!record) {
    const evidenceRef = readString(value);
    return evidenceRef ? { evidenceRef } : undefined;
  }
  return {
    ...readProofRef(record),
    ontologyVersion: readString(record.ontologyVersion),
    sourceComplianceMode: readString(record.sourceComplianceMode),
    sourceScope: readString(record.sourceScope),
    absentUiPolicy: readString(record.absentUiPolicy),
  };
}

function readApplicationProof(
  value: unknown,
): HeadlessApplicationProof | undefined {
  const record = readRecord(value);
  if (!record) {
    const cogoJsonRef = readString(value);
    return cogoJsonRef ? { cogoJsonRef } : undefined;
  }
  return {
    ...readProofRef(record),
    cogoJsonRef:
      readString(record.cogoJsonRef) ?? readString(record.applicationCogoJson),
    readiness:
      readString(record.readiness) ?? readString(record.applicationReadiness),
    surfaceCount: readNumber(record.surfaceCount),
    rawFigmaExtractionGapCount: readNumber(record.rawFigmaExtractionGapCount),
    sourceCoverageUnassembledFactCount: readNumber(
      record.sourceCoverageUnassembledFactCount,
    ),
    sourceAuthorityViolationCount: readNumber(
      record.sourceAuthorityViolationCount,
    ),
  };
}

function readRuntimeProof(value: unknown): HeadlessRuntimeProof | undefined {
  const record = readRecord(value);
  if (!record) {
    const evidenceRef = readString(value);
    return evidenceRef ? { evidenceRef } : undefined;
  }
  return {
    ...readProofRef(record),
    finalSurfaceRef: readString(record.finalSurfaceRef),
    finalStoryRef: readString(record.finalStoryRef),
    finalText: readString(record.finalText),
  };
}

function readVideoProof(value: unknown): HeadlessVideoProof | undefined {
  const record = readRecord(value);
  if (!record) {
    const evidenceRef = readString(value);
    return evidenceRef ? { evidenceRef } : undefined;
  }
  return {
    ...readRuntimeProof(record),
    videoRef: readString(record.videoRef),
    posterRef: readString(record.posterRef),
    accountUxNotMounted: readBooleanOrString(record.accountUxNotMounted),
  };
}

function readPolicy(value: unknown): HeadlessAuthorityPolicy | undefined {
  const record = readRecord(value);
  if (!record) return undefined;
  return {
    sourceComplianceMode: readString(record.sourceComplianceMode),
    excludedSourceScopes: readStringArray(record.excludedSourceScopes),
    modelMayPatchCogoJsonDirectly:
      typeof record.modelMayPatchCogoJsonDirectly === "boolean"
        ? record.modelMayPatchCogoJsonDirectly
        : undefined,
    studioMaySilentlyMutateCogoJson:
      typeof record.studioMaySilentlyMutateCogoJson === "boolean"
        ? record.studioMaySilentlyMutateCogoJson
        : undefined,
    changeRoute: readString(record.changeRoute),
  };
}

function readEvidenceRefs(value: unknown): Record<string, string> | undefined {
  const record = readRecord(value);
  if (!record) return undefined;
  const entries = Object.entries(record).filter(
    (entry): entry is [string, string] =>
      typeof entry[1] === "string" && entry[1].trim() !== "",
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function normalizeHeadlessAuthorityBundle(
  candidate: Record<string, unknown>,
): StudioHeadlessAuthorityBundle | null {
  if (candidate.schemaVersion === "studio-headless-authority-bundle.v1") {
    return {
      schemaVersion: "studio-headless-authority-bundle.v1",
      status: readString(candidate.status) ?? "unknown",
      artifactId: readString(candidate.artifactId),
      title: readString(candidate.title),
      generatedAt: readString(candidate.generatedAt),
      sourceDocumentProfile: readSourceDocumentProfile(
        candidate.sourceDocumentProfile,
      ),
      specFlowOntology: readSpecFlowOntology(candidate.specFlowOntology),
      application: readApplicationProof(candidate.application),
      runtimeE2E: readRuntimeProof(candidate.runtimeE2E),
      storyCoverage: readProofRef(candidate.storyCoverage),
      overlapAudit: readProofRef(candidate.overlapAudit),
      videoProof: readVideoProof(candidate.videoProof),
      policy: readPolicy(candidate.policy),
      evidenceRefs: readEvidenceRefs(candidate.evidenceRefs),
    };
  }

  const sourceDocumentProfile = readString(candidate.sourceDocumentProfile);
  const applicationCogoJson = readString(candidate.applicationCogoJson);
  const applicationAssemblyGate = readString(candidate.applicationAssemblyGate);
  const flowE2eGate = readString(candidate.flowE2eGate);
  const storyCoverageGate = readString(candidate.storyCoverageGate);
  const overlapAuditGate = readString(candidate.overlapAuditGate);
  const appPlayVideoProof = readString(candidate.appPlayVideoProof);
  if (
    !sourceDocumentProfile &&
    !applicationCogoJson &&
    !applicationAssemblyGate &&
    !flowE2eGate &&
    !storyCoverageGate &&
    !overlapAuditGate &&
    !appPlayVideoProof
  ) {
    return null;
  }

  return {
    schemaVersion: "studio-headless-authority-bundle.v1",
    status: readString(candidate.status) ?? "ready",
    artifactId:
      readString(candidate.artifactId) ??
      "application-headless-authority-bundle",
    title:
      readString(candidate.title) ?? "Application headless authority bundle",
    generatedAt: readString(candidate.generatedAt),
    sourceDocumentProfile: {
      status: readString(candidate.sourceDocumentProfileStatus),
      evidenceRef: sourceDocumentProfile,
      profileKind: readString(candidate.profileKind),
      sourceComplianceMode: readString(candidate.sourceComplianceMode),
    },
    application: {
      status: readString(candidate.applicationStatus),
      readiness: readString(candidate.applicationReadiness),
      cogoJsonRef: applicationCogoJson,
      evidenceRef: applicationAssemblyGate,
    },
    runtimeE2E: {
      status: readString(candidate.flowE2eStatus),
      evidenceRef: flowE2eGate,
      finalSurfaceRef: readString(candidate.finalSurfaceRef),
      finalStoryRef: readString(candidate.finalStoryRef),
      finalText: readString(candidate.finalText),
    },
    storyCoverage: {
      status: readString(candidate.storyCoverageStatus),
      evidenceRef: storyCoverageGate,
    },
    overlapAudit: {
      status: readString(candidate.overlapAuditStatus),
      evidenceRef: overlapAuditGate,
    },
    videoProof: {
      status: readString(candidate.videoProofStatus),
      evidenceRef: appPlayVideoProof,
      videoRef: readString(candidate.videoRef),
      posterRef: readString(candidate.posterRef),
      finalSurfaceRef: readString(candidate.finalSurfaceRef),
      finalText: readString(candidate.finalText),
      accountUxNotMounted: readBooleanOrString(candidate.accountUxNotMounted),
    },
    policy: {
      sourceComplianceMode: readString(candidate.sourceComplianceMode),
      modelMayPatchCogoJsonDirectly: false,
      studioMaySilentlyMutateCogoJson: false,
      changeRoute: "source/spec modification request and rerun headless gates",
    },
    evidenceRefs: {
      ...(sourceDocumentProfile ? { sourceDocumentProfile } : {}),
      ...(applicationCogoJson ? { applicationCogoJson } : {}),
      ...(applicationAssemblyGate ? { applicationAssemblyGate } : {}),
      ...(flowE2eGate ? { flowE2eGate } : {}),
      ...(storyCoverageGate ? { storyCoverageGate } : {}),
      ...(overlapAuditGate ? { overlapAuditGate } : {}),
      ...(appPlayVideoProof ? { appPlayVideoProof } : {}),
    },
  };
}

export function readProjectHeadlessAuthorityBundle(
  project: unknown,
): StudioHeadlessAuthorityBundle | null {
  for (const key of CANDIDATE_KEYS) {
    const candidate = readRecord(readProofField(project, key));
    if (!candidate) continue;
    const bundle = normalizeHeadlessAuthorityBundle(candidate);
    if (bundle) return bundle;
  }
  return null;
}

export function headlessAuthorityStatus(
  bundle: StudioHeadlessAuthorityBundle,
): string {
  return (
    bundle.status ||
    bundle.application?.readiness ||
    bundle.sourceDocumentProfile?.status ||
    bundle.runtimeE2E?.status ||
    "unknown"
  );
}
